import { useEffect, useRef } from "react"
import { Toaster as Sonner, toast, useSonner, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

// Sonner's default toast lifetime (TOAST_LIFETIME). Not exported by the lib.
const DEFAULT_DURATION_MS = 4_000
const GRACE_MS = 1_000

/**
 * Audit R3-12 backstop. sonner v2 pauses every toast's auto-dismiss timer
 * while `document.hidden` is true (hardcoded, no opt-out prop in 2.0.7), so a
 * toast fired in a hidden/backgrounded tab never starts its timer and
 * survives SPA navigations until a hard reload. This watchdog hard-caps such
 * toasts at duration + grace with a plain setTimeout. Browsers throttle
 * background-tab timeouts, so in a still-hidden tab the dismissal may land
 * late — but it always lands, and promptly once the tab is refocused.
 *
 * Audit R4-3: armed ONLY for toasts created while the tab is hidden — the one
 * wedge sonner can't time out. Visible-tab toasts keep sonner's full
 * hover-pause UX; an unconditional watchdog would dismiss a toast under the
 * user's cursor.
 */
function ToastAutoDismissWatchdog() {
  const { toasts } = useSonner()
  const scheduled = useRef(new Set<string | number>())
  useEffect(() => {
    if (!document.hidden) return
    for (const t of toasts) {
      if (scheduled.current.has(t.id)) continue
      if (t.type === "loading" || t.duration === Infinity) continue
      scheduled.current.add(t.id)
      setTimeout(() => {
        scheduled.current.delete(t.id)
        toast.dismiss(t.id)
      }, (t.duration ?? DEFAULT_DURATION_MS) + GRACE_MS)
    }
  }, [toasts])
  return null
}

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <>
    <ToastAutoDismissWatchdog />
    <Sonner
      theme="dark"
      className="toaster group"
      icons={{
        success: (
          <CircleCheckIcon className="size-4" />
        ),
        info: (
          <InfoIcon className="size-4" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4" />
        ),
        error: (
          <OctagonXIcon className="size-4" />
        ),
        loading: (
          <Loader2Icon className="size-4 animate-spin" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
        },
      }}
      {...props}
    />
    </>
  )
}

export { Toaster }
